// Adds / Removes crumbs and publishes updates
// Handles crumb selection publishing
// Handles crumb updates to show different shortcuts based on the page that the user has displayed


import { Injectable, OnInit, OnDestroy } from '@angular/core';
import { KeyValue } from '@angular/common';

import { Subject, Subscription } from 'rxjs';

import { Breadcrumb } from '../models/breadcrumb.model';
import { NavigationItem } from '../models/navigation-item.model';
import { LoggingMessageService } from './logging-message.service';
import { UserNavigationService } from './user-navigation.service';
import { PermIdIndexerEnum } from '../models/permid-indexer-enum';
import { NavigationUtils } from '../navigation-utils';


@Injectable({
    providedIn: 'root'
})
export class BreadcrumbService implements OnInit, OnDestroy {
    private $subscriptions: Subscription = new Subscription();

    private crumbAddedSource = new Subject<Breadcrumb>();
    public crumbAdded$ = this.crumbAddedSource.asObservable();

    private crumbRemovedSource = new Subject<Breadcrumb>();
    public crumbRemoved$ = this.crumbRemovedSource.asObservable();

    private crumbSelectedSource = new Subject<KeyValue<string, string>>();
    public crumbSelected$ = this.crumbSelectedSource.asObservable();

    private crumbShortcutsChangedSource = new Subject<Breadcrumb>();
    public crumbShortcutsChanged$ = this.crumbShortcutsChangedSource.asObservable();

    private _breadcrumbs: Array<Breadcrumb>;
    get Breadcrumbs(): Array<Breadcrumb> {
        if (!this._breadcrumbs) {
            this._breadcrumbs = new Array<Breadcrumb>();
        }
        return this._breadcrumbs;
    }
    set Breadcrumbs(value: Breadcrumb[]) {
       this._breadcrumbs = value;
    }

    get LastCrumb(): Breadcrumb {
        return this._breadcrumbs[this._breadcrumbs.length - 1];
    }

    constructor( private navigationService: UserNavigationService, private logger: LoggingMessageService) { }

    ngOnInit(): void {

    }

    public setBreadcrumbs(navItem: NavigationItem) {
        if (navItem && navItem.PermItemID) {
            const stepsBack = this.numberOfStepsBack(navItem.PermItemID);
            const isExistingCrumb = (stepsBack >= 0);
            if ((isExistingCrumb === true) && (stepsBack > 0)) {
                this.removeCrumbs(stepsBack);
            }
            // Handle sibling/cousin switch: find the nearest ancestor in breadcrumbs
            // and remove back to that level, then add intermediate levels
            if (!isExistingCrumb) {
                let ancestorPermId = NavigationUtils.GetParentPermId(navItem.PermItemID);
                const pathToAdd: string[] = []; // Track intermediate levels to add
                
                while (ancestorPermId && ancestorPermId.length > 0) {
                    const ancestorStepsBack = this.numberOfStepsBack(ancestorPermId);
                    if (ancestorStepsBack > 0) {
                        this.removeCrumbs(ancestorStepsBack);
                        break;
                    }
                    if (ancestorStepsBack === 0) {
                        // Ancestor is the last crumb, no need to remove
                        break;
                    }
                    // Ancestor not in breadcrumbs, track it and go up another level
                    pathToAdd.unshift(ancestorPermId);
                    ancestorPermId = NavigationUtils.GetParentPermId(ancestorPermId);
                }
                
                // Add intermediate levels that were missing
                const fullTree = this.navigationService.CachedNavigationMenu;
                if (fullTree) {
                    for (const permId of pathToAdd) {
                        const intermediateItem = fullTree.getNavigationItemUsingPermID(permId);
                        if (intermediateItem && intermediateItem.Items && intermediateItem.Items.length > 0) {
                            this.addCrumb(intermediateItem);
                        }
                    }
                }
            }
            
            const fullTree = this.navigationService.CachedNavigationMenu;
            if (fullTree) {
                const ni = fullTree.getNavigationItemUsingPermID(navItem.PermItemID);
                if (!isExistingCrumb && ni && ni.Items && ni.Items.length > 0) {
                    this.addCrumb(ni);
                }
            }
        }
    }

    public containsUnit(permItemID: string): boolean {
        let contains = false;
        const parts = permItemID.split('_');
        if (parts.length >= PermIdIndexerEnum.Unit) {
            const unitId = parts[PermIdIndexerEnum.Unit];
            this.Breadcrumbs.forEach(crumb => {
                const isUnitCrumb = Breadcrumb.isUnitDashboardCrumb(crumb.Item.key);
                if (isUnitCrumb) {
                    contains = crumb.Item.key.endsWith(unitId);
                }
            });
        }
        return contains;
    }

    public getPenultimate(): Breadcrumb {
        let penultimate: Breadcrumb;
        const length = this.breadcrumbLength();
        if (length > 1) {
            penultimate = this.Breadcrumbs[length - 2];
        }
        return penultimate;
    }

    public numberOfStepsBack(permItemID: string): number {
        let stepsBack = Number.NEGATIVE_INFINITY;
        const length = this.breadcrumbLength();
        const index = this.breadcrumbIndex(permItemID);
        if (index >= 0) {
            stepsBack = (length - 1) - index;
        }
        return stepsBack;
    }

    private breadcrumbLength() {
        return this.Breadcrumbs.length;
    }

    private breadcrumbIndex(permItemID: string) {
        const index = this.Breadcrumbs.findIndex(c => (c.Item.key === permItemID));
        return index;
    }

    public clearBreadcrumbs() {
        const length = this.breadcrumbLength();
        //this.removeCrumbs(length - 1);
        this.removeCrumbs(length);
        this.Breadcrumbs = new Array<Breadcrumb>();
    }

    public addCrumb(navItem: NavigationItem) {
        const crumb = this.createBreadcrumb(navItem);
        this.Breadcrumbs.push(crumb);

        this.publishCrumbAdded(crumb);

        const isIndicatorCrumb = Breadcrumb.isIndicatorCrumb(crumb.Item.key);
        if (isIndicatorCrumb) {
            this.updateUnitCrumbShortcuts();
        }
    }

    public removeCrumbs(amount: number) {
        for (let index = 0; index < amount; index++) {
           this.removeCrumb();
        }
    }

    public crumbSelected(crumbInfo: KeyValue<string, string>) {
        // const steps = this.numberOfStepsBack(crumb.Item.key);
        // this.removeCrumbs(steps);

        this.publishCrumbSelected(crumbInfo);
    }

    private removeCrumb() {
        const crumb = this.Breadcrumbs.pop();
        this.publishCrumbRemoved(crumb);

        const isIndicatorCrumb = Breadcrumb.isIndicatorCrumb(crumb.Item.key);
        if (isIndicatorCrumb) {
            this.updateUnitCrumbShortcuts();
        }
    }

    public setHomeBreadcrumb() {
        const userNav = this.navigationService.DisplayedNavigationMenu;
        const homeNavItem = userNav.Items[0];
        this.addCrumb(homeNavItem);
    }

    private createBreadcrumb(navItem: NavigationItem): Breadcrumb {
        const kv: KeyValue<string, string> = { key: navItem.PermItemID, value: navItem.ItemName };
        const crumb = new Breadcrumb(kv);
        this.setShortcuts(crumb);
        crumb.Children = this.getChildren(navItem);
        return crumb;
    }

    private setShortcuts(crumb: Breadcrumb) {
        const allShortcuts = new Array<KeyValue<string, string>>();
        if (this.Breadcrumbs && this.Breadcrumbs.length > 0) {
            const parentCrumb = this.Breadcrumbs[this.Breadcrumbs.length - 1];
            if (parentCrumb) {
                const siblings = parentCrumb.Children;
                siblings.forEach(sib => {
                    const sibling: KeyValue<string, string> = { key: sib.key, value: sib.value };
                    allShortcuts.push(sibling);
                });
            }
        }
        const shortcuts = this.filterShortcuts(crumb, allShortcuts);
        crumb.ShortcutItems = shortcuts;
    }

    private filterShortcuts(crumb: Breadcrumb, unfiltered: KeyValue<string, string>[]): KeyValue<string, string>[] {
        const allOtherItems = unfiltered.filter(sc => sc.key !== crumb.Item.key);

        const parts = crumb.Item.key.split('_');
        const lastPart = parts[parts.length - 1];
        const prefix = `_${lastPart.substr(0, 1)}`;
        const sameTypeItems = allOtherItems.filter(i => i.key.includes(prefix));

        this.modifyUnitLabel(sameTypeItems);
        return sameTypeItems;
    }

    private modifyUnitLabel(items: KeyValue<string, string>[]) {
        items.forEach(oi => {
            oi.value = Breadcrumb.getLabel(oi);
        });
    }

    private  getChildren(navItem: NavigationItem): KeyValue<string, string>[]  {
        const children = new Array<KeyValue<string, string>>();
        navItem.Items.forEach(i => {
            const kv: KeyValue<string, string> = { key: i.PermItemID, value: i.ItemName };
            children.push(kv);
        });
        return children;
    }

    private publishCrumbAdded(crumb: Breadcrumb) {
        this.crumbAddedSource.next(crumb);
    }

    private publishCrumbRemoved(crumb: Breadcrumb) {
        this.crumbRemovedSource.next(crumb);
    }

    private publishCrumbSelected(crumbInfo: KeyValue<string, string>) {
        this.crumbSelectedSource.next(crumbInfo);
    }

    private updateUnitCrumbShortcuts() {
        // Switch shortcuts on Unit crumb to use the indicator shortcuts when the last crumb is an indicator
        this.Breadcrumbs.forEach(crumb => {
            const isUnit = Breadcrumb.isUnitDashboardCrumb(crumb.Item.key);
            if (isUnit) {
                this.updateUnitCrumbIndicatorShortcuts(crumb);
                const lastCrumb = this.LastCrumb;
                const isLastCrumbIndicator = Breadcrumb.isIndicatorCrumb(lastCrumb.Item.key);
                crumb.DisplayIndicatorShortcuts = isLastCrumbIndicator;
                this.publishShortcutsChanged(crumb);
            }
        });
    }

    private updateUnitCrumbIndicatorShortcuts(unitCrumb: Breadcrumb) {
        try {
        const indicatorShortcuts = new Array<KeyValue<string, string>>();
        const parts = this.LastCrumb.Item.key.split('_');
        // const indicator = parts[3];
        const indicator = parts[PermIdIndexerEnum.Indicator];
        const otherUnits = unitCrumb.RegularShortcutItems;
        otherUnits.forEach(unitShortcut => {
            const shortcutIndicatorPermId = `${unitShortcut.key}_${indicator}`;
            const menuItems = this.navigationService.DisplayedMenuItems;
            let shortcutIndicatorItem = this.navigationService.getMenuItem(shortcutIndicatorPermId, menuItems);
            if (!shortcutIndicatorItem) {
                const unitSplit = this.LastCrumb.Item.key.split('_u');
                const hosp = unitSplit[0];
                const isFacilityInCache = this.navigationService.UnitItemCacheExists(hosp);
                if (isFacilityInCache) {
                    const unitCacheItems = this.navigationService.UnitMenuItemCache.get(hosp);
                    shortcutIndicatorItem = this.navigationService.getMenuItem(shortcutIndicatorPermId, unitCacheItems);
                }
            }
            if (shortcutIndicatorItem) {
                // change shortcut link
                const indValue = `${unitShortcut.value} - I`;
                const kv: KeyValue<string, string> = { key: shortcutIndicatorPermId, value: indValue };
                indicatorShortcuts.push(kv);
            } else {
                indicatorShortcuts.push(unitShortcut);
            }
        });
   
        unitCrumb.IndicatorShortcutItems = indicatorShortcuts;
    } catch (ex) {
        const m = ex;
    }
    }

    private publishShortcutsChanged(crumb: Breadcrumb) {
        this.crumbShortcutsChangedSource.next(crumb);
    }

    unsubscribe() {
        if (this.$subscriptions) {
            this.$subscriptions.unsubscribe();
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe();
    }
}

